/* eslint-disable max-lines*/
import {
    useState, useEffect, useContext
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    Home, MoreVertical, RefreshCcw, Trash2, Plus, BookOpen,
} from 'lucide-react';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from 'shared/components/ui/dropdown-menu';
import {
    Input
} from 'shared/components/ui/input';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Button
} from 'shared/components/ui/button';
import {
    Separator
} from 'shared/components/ui/separator';
import {
    AppLoader
} from 'shared/components/LoaderSpinner';
import AlertModal from 'shared/components/Alertmodal';
import {
    AddTableForm
} from './AddTableForm';
import {
    useRouting
} from './RoutingContext';
import {
    schemaService
} from 'shared/api/services/BEXO';
import {
    Context
} from 'shared/context/GlobalState';
import {
    useRecordsData
} from './table-records/RecordsData';

// Helper to convert a string to Pascal Case (for table name)
function toPascalCase(str) {
    return str
        .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => index === 0 ? match.toUpperCase() : match.toUpperCase())
        .replace(/\s+/g, '');
}

function formatFieldName(fieldName) {
    return fieldName
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)(?=[A-Z][a-z])/g, '$1 ')
        .replace(/([A-Z]+)(?=[A-Z]+$)/g, '$1')
        .replace(/^./, (str) => str.toUpperCase())
        .replace(/\s+/g, ' ');
}

export function TableSidebar() {
    const {
        triggerReload
    } = useRouting();
    const {
        currentTable, fetchTables, tables, isLoading, setIsLoading
    } = useRouting();
    const {
        oragID, appId
    } = useParams();
    const {
        addNewNotifcation
    } = useContext(Context);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTables, setSelectedTables] = useState([]);
    const [hoveredId, setHoveredId] = useState(null);
    const [delModal, setDelModal] = useState(false);
    const [delBtn, setDelBtn] = useState(false);
    const [tableToDelete, setTableToDelete] = useState(null);
    const navigate = useNavigate();

    const [accessRolesData, setAccessRolesData] = useState([]);
    const {
        fetchAllRecords
    } = useRecordsData('UserRole');

    useEffect(() => {
        async function loadRecords() {
            try {
                const result = await fetchAllRecords();
                const resRecords = result.records;
                const accessRoles = resRecords.map(({
                    recordId, roleName
                }) => ({
                    value: recordId,
                    label: roleName,
                }));
                setAccessRolesData(accessRoles || []);
            } catch (err) {
                console.error('Failed to fetch records:', err);
            }
        }
        loadRecords();
    }, []);

    useEffect(() => {
        fetchTables();
    }, []);

    const handleNavigate = (id) => navigate(`./${id}`);

    const handleAddTable = async (tableName) => {
        const pascalCaseTableName = toPascalCase(tableName); // Convert table name to Pascal Case

        // Map over accessRolesData and extract roleName and recordId
        const roleMapping = accessRolesData.reduce((acc, {
            value, label
        }) => {
            acc[label] = value;
            return acc;
        }, {});

        const payload = {
            schemas: [
                {
                    tableName: pascalCaseTableName, // Send the Pascal Case table name
                    columns: [
                        {
                            columnName: 'recordId',
                            dataType: 'string',
                            length: 255,
                            constraints: ['PRIMARY KEY', 'NOT NULL', 'INDEX', 'CASESENSITIVE'],
                            fieldsAccessBy: [roleMapping['Admin']],
                            description: `In ${tableName} Table, A unique identifier for each user record in the table.`,
                            readOnly: true,
                        },
                        {
                            columnName: 'createdOn',
                            dataType: 'datetime',
                            length: 0,
                            format: 'YYYY-MM-DD HH:MM:SS',
                            constraints: ['NOT NULL', 'INDEX'],
                            fieldsAccessBy: [roleMapping['Admin']],
                            description: `In ${tableName} Table, The date and time when the user record was created.`,
                            readOnly: true,
                        },
                        {
                            columnName: 'modifiedOn',
                            dataType: 'datetime',
                            length: 0,
                            format: 'YYYY-MM-DD HH:MM:SS',
                            default: 'CURRENT_TIMESTAMP',
                            constraints: ['NOT NULL', 'INDEX'],
                            fieldsAccessBy: [roleMapping['Admin']],
                            description: `In ${tableName} Table, The date and time when the user record was last modified, defaulting to the current timestamp.`,
                            readOnly: true,
                        },
                        {
                            columnName: 'createdBy',
                            dataType: 'string',
                            length: 255,
                            constraints: ['FOREIGN KEY', 'NOT NULL'],
                            references: {
                                type: 'one-to-one',
                                table: 'users',
                                column: 'recordId',
                            },
                            fieldsAccessBy: [roleMapping['Admin']],
                            description: `In ${tableName} Table, The identifier of the user or entity that created the order record.`,
                            readOnly: true,
                        },
                        {
                            columnName: 'modifiedBy',
                            dataType: 'string',
                            length: 255,
                            constraints: ['FOREIGN KEY', 'NOT NULL'],
                            references: {
                                type: 'one-to-one',
                                table: 'users',
                                column: 'recordId',
                            },
                            fieldsAccessBy: [roleMapping['Admin']],
                            description: `In ${tableName} Table, The identifier of the user or entity that last modified the order record.`,
                            readOnly: true,
                        },
                        {
                            columnName: 'recordStatus',
                            dataType: 'enum',
                            length: 0,
                            values: ['created'],
                            fieldsAccessBy: [roleMapping['Admin']],
                            description: `In ${tableName} Table, The current status of the record.`,
                            readOnly: true,
                        }
                    ],
                },
            ],
        };

        try {
            setIsLoading(true);
            await schemaService.createSchemas(oragID, appId, payload);

            addNewNotifcation('Table Created Successfully', 'success');
            fetchTables();
            triggerReload();
            handleNavigate(pascalCaseTableName); // Navigate to the newly created table
        } catch (error) {
            console.error('Error creating table: ', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTable = async (name) => {
        try {
            setIsLoading(true);
            setDelBtn(true);
            const payload = {
                schemas: [{
                    tableName: name
                }]
            };
            await schemaService.deleteSchemas(payload, oragID, appId);
            addNewNotifcation('Deleted Table Successfully', 'success');
            fetchTables();
            triggerReload();
            if (currentTable === name) handleNavigate('../tables');
        } catch {
            addNewNotifcation('Failed to delete table', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const confirmDeleteSelected = async () => {
        const payload = {
            schemas: selectedTables.map(name => ({
                tableName: name
            }))
        };
        try {
            setIsLoading(true);
            await schemaService.deleteSchemas(payload, oragID, appId);
            addNewNotifcation('Deleted Tables Successfully', 'success');
            fetchTables();
            triggerReload();
        } catch {
            addNewNotifcation('Failed to delete tables', 'error');
        } finally {
            setSelectedTables([]);
            setDelModal(false);
            setIsLoading(false);
        }
    };

    const toggleSelect = (id) => {
        setSelectedTables((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const showAllCheckboxes = selectedTables.length > 0;

    return (
        <div className="border-r bg-background w-64 flex flex-col h-full">
            <div className="p-4 space-y-4">
                <Input
                    placeholder="Search tables..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-9"
                />
            </div>

            {/* Dashboard */}
            <div
                className={`group flex items-center rounded-md mx-2 px-2 py-2 text-sm transition-colors cursor-pointer w-[90%]
          ${currentTable === 'dashboard' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/50'}`}
                onClick={() => handleNavigate('../tables')}
            >
                <div className="flex items-center gap-3 flex-1">
                    <Home className="h-4 w-4 shrink-0" />
                    <span className="truncate w-full">Dashboard</span>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 ml-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => handleNavigate('create-table-ai')}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Table
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleNavigate('blueprints')}>
                            <BookOpen className="mr-2 h-4 w-4" />
                            Blueprints
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <Separator className="my-2" />

            <div className="px-4 py-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-muted-foreground">Tables</h2>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={fetchTables}>
                    <RefreshCcw
                        className="h-4 w-4"
                        style={{
                            animation: isLoading ? 'spin 1s linear infinite' : 'none'
                        }}
                    />
                </Button>
            </div>

            {isLoading ? (
                <AppLoader className="h-8 w-8" />
            ) : (
                <ScrollArea className="flex-1 w-full">
                    <div className="space-y-1 p-2">
                        {tables
                            .filter((table) =>
                                table.name.toLowerCase().includes(searchTerm.toLowerCase())
                            )
                            .map((table) => {
                                const Icon = table.icon;
                                const isActive = currentTable === table.id;
                                const isSelected = selectedTables.includes(table.id);
                                const showCheckbox = showAllCheckboxes || hoveredId === table.id;

                                return (
                                    <div
                                        key={table.id}
                                        onMouseEnter={() => setHoveredId(table.id)}
                                        onMouseLeave={() => setHoveredId(null)}
                                        className={`group flex items-center rounded-md px-2 py-2 text-sm transition-colors cursor-pointer w-[90%] mx-2 ${isActive ? 'bg-primary text-primary-foreground' : isSelected ? 'bg-muted' : 'hover:bg-muted/50'}`}
                                        onClick={() => handleNavigate(table.id)}
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            {showCheckbox ? (
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelect(table.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <Icon className="h-4 w-4 shrink-0" />
                                            )}
                                            <span className="truncate flex-1">{formatFieldName(table.name)}</span>
                                        </div>

                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 opacity-0 group-hover:opacity-100 ml-auto"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTableToDelete(table.id);
                                                setDelModal(true);
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                    </div>
                                );
                            })}
                    </div>
                </ScrollArea>
            )}

            {selectedTables.length > 0 && (
                <div className="p-4 border-t">
                    <Button variant="destructive" className="w-full" onClick={() => setDelModal(true)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Selected Tables ({selectedTables.length})
                    </Button>
                </div>
            )}

            <div className="p-4 border-t">
                <AddTableForm onAddTable={handleAddTable} tables={tables} isLoading={isLoading} />
            </div>

            <AlertModal
                loading={delBtn}
                openModal={delModal}
                setopenModal={setDelModal}
                title={`Are you sure you want to delete ${selectedTables}?`}
                onDelete={async () => {
                    if (tableToDelete) {
                        await handleDeleteTable(tableToDelete); // Pass the selected table to be deleted
                    } else {
                        await confirmDeleteSelected(); // Delete selected tables
                    }
                    setDelModal(false); // Close the modal after deleting
                }}
            >
                <ul className="text-sm text-muted list-disc pl-6">
                    <li>{tables.find((table) => table.id === tableToDelete)?.name}</li>
                </ul>
            </AlertModal>
        </div>
    );
}